// libs
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
// types
import { Reducers } from 'store/types'
import { IDashboard, IDashboardItemSettings, IGetDashboardItemsResponseItem } from 'types/graphql'
// utils
import { isBoolean } from 'lodash'

export type ExtendedDashboard = IDashboard & { widgets?: IGetDashboardItemsResponseItem[] }
export type Widget = IGetDashboardItemsResponseItem

export interface DashboardsState {
  edit: boolean
  data: IDashboard[]
  current: ExtendedDashboard | null
}

const initialState: DashboardsState = {
  edit: false,
  data: [],
  current: null
}

export const dashboardsSlice = createSlice({
  name: Reducers.DASHBOARDS,
  initialState,
  reducers: (create) => ({
    addWidget: create.reducer((state, action: PayloadAction<Widget>) => {
      state.current = {
        ...state.current,
        widgets: [...(state.current.widgets || []), action.payload]
      }
    }),
    updateWidget: create.reducer((state, action: PayloadAction<{ id: string; data: IDashboardItemSettings }>) => {
      state.current = {
        ...state.current,
        widgets: state.current.widgets.map((widget) => {
          if (widget.item.id === action.payload.id) {
            return { ...widget, item: { ...widget.item, settings: action.payload.data } }
          }
          return widget
        })
      }
    }),
    removeWidget: create.reducer((state, action: PayloadAction<string>) => {
      state.current = {
        ...state.current,
        widgets: state.current.widgets.filter((widget) => widget.item.id !== action.payload)
      }
    }),
    addDashboard: create.reducer((state, action: PayloadAction<IDashboard>) => {
      state.data = [...state.data, action.payload]
    }),
    updateDashboard: create.reducer((state, action: PayloadAction<{ id: string; data: IDashboard }>) => {
      state.data = state.data.map((item) => {
        if (item.id === action.payload.id) {
          return { ...item, ...action.payload.data }
        }
        return item
      })
    }),
    removeDashboard: create.reducer((state, action: PayloadAction<string>) => {
      state.data = state.data.filter((item) => item.id !== action.payload)
    }),
    setDashboardsData: create.reducer((state, action: PayloadAction<Partial<DashboardsState>>) => {
      state.data = action.payload.data || state.data
      state.edit = isBoolean(action.payload.edit) ? action.payload.edit : state.edit
      state.current = action.payload.current || state.current
    }),
    updateCurrentDashboard: create.reducer((state, action: PayloadAction<IGetDashboardItemsResponseItem[]>) => {
      state.current = { ...state.current, widgets: action.payload }
    })
  }),
  selectors: {
    dashboardsSelector: (state) => state.data,
    isDashboardEditSelector: (state) => state.edit,
    currentDashboardSelector: (state) => state.current
  }
})

export const {
  addWidget,
  updateWidget,
  removeWidget,
  addDashboard,
  updateDashboard,
  removeDashboard,
  setDashboardsData,
  updateCurrentDashboard
} = dashboardsSlice.actions

export const { dashboardsSelector, isDashboardEditSelector, currentDashboardSelector } = dashboardsSlice.selectors
